// src/views/Login/ResetPassword.tsx

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokenValidation } from '@/domains/auth/hooks/useTokenValidation';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import AuthLayout from '@/components/AppComponents/Layout/AuthLayout';
import i18n from '@/shared/i18n/i18n';
import { Form } from '@/components/AppComponents/Form/Form';
import {
  ResetPasswordFormValues,
  resetPasswordSchema,
} from './schemas/forgotPassword.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { TextInput } from '@/components/UI/components/Textfield';
import { Button } from '@/components/UI/components/Button';
import { useAuthStore } from '@/domains/auth/auth.store';
import {
  otpForgotPasswordUseCase,
  useChangePasswordUseCase,
} from '@/domains/auth/auth.usecases';
import { OtpFormValues, otpSchema } from './schemas/login.schema';
import { Callout } from '@/components/UI/components/Callout';
import { RiCheckboxCircleFill } from '@remixicon/react';
import { usePasswordStrength } from '@/domains/auth/hooks/usePasswordStrength';
import { TrafficLights } from '@/components/UI/components/TrafficLights';
import { Icon } from '@/components/UI/components/Icon';

const ResetPassword = () => {
  const [formValues, setFormValues] = useState<ResetPasswordFormValues | null>(
    null,
  );
  const [isLoadingPwd, setIsLoadingPwd] = useState(false);
  const [isSentPwd, setIsSentPwd] = useState(false);
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [otpIsSent, setOtpIsSent] = useState(false);
  const { token, isValid, error } = useTokenValidation();
  const navigate = useNavigate();
  const otpForgotPassword = otpForgotPasswordUseCase();
  const changePassword = useChangePasswordUseCase();
  const { setTokens, clearTokens } = useAuthStore();

  useEffect(() => {
    if (error)
      navigate(NAVBAR_SOURCES.forgotPassword, { state: { message: error } });
  }, [error, navigate]);

  useEffect(() => {
    if (token && isValid) {
      setTokens(token, '');
    }
  }, [token, isValid, setTokens]);

  const resetPasswordMethods = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const otpMethods = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
  });

  const password = resetPasswordMethods.watch('password');
  const { color, label, strength, acceptance } = usePasswordStrength(password);

  const handleResetPassword = async (data: ResetPasswordFormValues) => {
    setFormValues(data);
    try {
      setIsLoadingPwd(true);
      await otpForgotPassword();
      setIsSentPwd(true);
    } catch (er) {
      console.error(er);
    } finally {
      setIsLoadingPwd(false);
    }
  };

  const handleOtpSubmit = async (data: OtpFormValues) => {
    if (!formValues) return;
    try {
      setIsLoadingOtp(true);
      await changePassword({ password: formValues.password, otp: data.otp });
      setOtpIsSent(true);
      clearTokens();
    } catch (er) {
      console.error(er);
    } finally {
      setIsLoadingOtp(false);
    }
  };

  return (
    <AuthLayout title={i18n.t('resetPassword.title')}>
      {otpIsSent ? (
        <>
          <div className="flex flex-col justify-start items-stretch space-y-8">
            <Callout
              title={i18n.t('resetPassword.successMessage')}
              icon={RiCheckboxCircleFill}
              color="green"
            />
            <Button
              variant="primary"
              onClick={() => navigate(NAVBAR_SOURCES.login)}
              className="w-full"
            >
              {i18n.t('forgotPassword.back')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="mb-6 text-gray-200">
            <p>{i18n.t('resetPassword.sentence')}</p>
            <ul>
              <li className="flex items-center">
                <Icon
                  icon={RiCheckboxCircleFill}
                  color={acceptance.uppercase ? 'green' : 'gray'}
                  className="transition-all duration-300"
                />
                <span
                  className={
                    acceptance.uppercase ? 'text-gray-200' : 'text-gray-300'
                  }
                >
                  {i18n.t('generic.passwordRules.uppercase')}
                </span>
              </li>
              <li className="flex items-center">
                <Icon
                  icon={RiCheckboxCircleFill}
                  color={acceptance.lowercase ? 'green' : 'gray'}
                  className="transition-all duration-300"
                />
                <span
                  className={
                    acceptance.lowercase ? 'text-gray-200' : 'text-gray-300'
                  }
                >
                  {i18n.t('generic.passwordRules.lowercase')}
                </span>
              </li>
              <li className="flex items-center">
                <Icon
                  icon={RiCheckboxCircleFill}
                  color={acceptance.number ? 'green' : 'gray'}
                  className="transition-all duration-300"
                />
                <span
                  className={
                    acceptance.number ? 'text-gray-200' : 'text-gray-300'
                  }
                >
                  {i18n.t('generic.passwordRules.number')}
                </span>
              </li>
              <li className="flex items-center">
                <Icon
                  icon={RiCheckboxCircleFill}
                  color={acceptance.special ? 'green' : 'gray'}
                  className="transition-all duration-300"
                />
                <span
                  className={
                    acceptance.special ? 'text-gray-200' : 'text-gray-300'
                  }
                >
                  {i18n.t('generic.passwordRules.special')}
                </span>
              </li>
              <li className="flex items-center">
                <Icon
                  icon={RiCheckboxCircleFill}
                  color={acceptance.length ? 'green' : 'gray'}
                  className="transition-all duration-300"
                />
                <span
                  className={
                    acceptance.length ? 'text-gray-200' : 'text-gray-300'
                  }
                >
                  {i18n.t('generic.passwordRules.length')}
                </span>
              </li>
            </ul>
          </div>
          <Form<ResetPasswordFormValues>
            onSubmit={handleResetPassword}
            methods={resetPasswordMethods}
          >
            <div className="flex flex-col justify-between space-y-8">
              <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                <label htmlFor="password" className="label text-gray-200">
                  {i18n.t('resetPassword.password')}
                </label>
                <TextInput
                  type="password"
                  autoFocus
                  placeholder=""
                  disabled={isSentPwd}
                  {...resetPasswordMethods.register('password')}
                  error={
                    !!resetPasswordMethods.formState.errors.password?.message
                  }
                  errorMessage={
                    resetPasswordMethods.formState.errors.password?.message
                  }
                />
                <TrafficLights
                  color={color}
                  label={label}
                  maxStrength={5}
                  strength={strength}
                />
              </div>
              <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                <label
                  htmlFor="confirmPassword"
                  className="label text-gray-200"
                >
                  {i18n.t('resetPassword.passwordConfirm')}
                </label>
                <TextInput
                  type="password"
                  placeholder=""
                  disabled={isSentPwd}
                  {...resetPasswordMethods.register('confirmPassword')}
                  error={
                    !!resetPasswordMethods.formState.errors.confirmPassword
                      ?.message
                  }
                  errorMessage={
                    resetPasswordMethods.formState.errors.confirmPassword
                      ?.message
                  }
                />
              </div>
              <Button
                type="submit"
                loading={isLoadingPwd}
                disabled={isSentPwd}
                variant="primary"
                className="w-full"
              >
                {i18n.t('resetPassword.submit')}
              </Button>
            </div>
          </Form>
          {/* OTP FORM */}
          {isSentPwd && (
            <Form<OtpFormValues>
              onSubmit={handleOtpSubmit}
              methods={otpMethods}
            >
              <div className="flex flex-col justify-between mt-6 pt-4 border-t space-y-8 border-revving-border dark:border-darkRevving-border">
                <div className="space-y-4">
                  <p>{i18n.t('resetPassword.otpSentence')}</p>
                  <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                    <label
                      htmlFor="otp"
                      className="text-sm font-accent text-gray-700 pl-1"
                    >
                      {i18n.t('login.otp')}
                    </label>
                    <TextInput
                      type="text"
                      autoFocus
                      {...otpMethods.register('otp')}
                      error={!!otpMethods.formState.errors.otp}
                      errorMessage={otpMethods.formState.errors.otp?.message}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  id="otp-submit"
                  loading={isLoadingOtp}
                  disabled={isLoadingOtp}
                  variant="primary"
                  className="w-full"
                >
                  {i18n.t('resetPassword.otpSubmit')}
                </Button>
              </div>
            </Form>
          )}
        </>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
